/**
 * Observability Utilities
 * Centralized logging, error tracking, and performance monitoring
 * Ready for Sentry/external service integration when API key is available
 * 
 * For Cloud Logging compatible structured logs, use:
 * import { structuredLogger } from "@/lib/logging/structuredLogger";
 */

import { 
  generateCorrelationId as genCorrId,
  getCorrelationId,
  setCorrelationId,
  structuredLogger 
} from "@/lib/logging/structuredLogger";

// Re-export structured logger utilities
export { structuredLogger, getCorrelationId, setCorrelationId };
export { generateCorrelationId as generateTraceId } from "@/lib/logging/structuredLogger";

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
  errorId?: string;
}

interface PerformanceMark {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
}

// In-memory log buffer for debugging (last 100 entries)
const logBuffer: LogEntry[] = [];
const MAX_LOG_BUFFER = 100;

// Performance marks
const performanceMarks: Map<string, PerformanceMark> = new Map();

/**
 * Generate unique error ID for tracking
 */
export function generateErrorId(): string {
  return `err_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Generate correlation ID for request tracing
 */
export function generateCorrelationId(): string {
  return genCorrId();
}

/**
 * Core logging function
 */
function log(level: LogLevel, message: string, context?: Record<string, unknown>): void {
  const entry: LogEntry = {
    level,
    message,
    timestamp: new Date().toISOString(),
    context,
  };

  if (level === 'error') {
    entry.errorId = generateErrorId();
  }

  // Add to buffer
  logBuffer.push(entry);
  if (logBuffer.length > MAX_LOG_BUFFER) {
    logBuffer.shift();
  }

  // Console output with styling
  const styles: Record<LogLevel, string> = {
    debug: 'color: gray',
    info: 'color: blue',
    warn: 'color: orange',
    error: 'color: red; font-weight: bold',
  };

  const prefix = `[${entry.timestamp}] [${level.toUpperCase()}]`;
  
  if (import.meta.env.DEV || level === 'error' || level === 'warn') {
    if (context) {
      console[level](`%c${prefix}`, styles[level], message, context);
    } else {
      console[level](`%c${prefix}`, styles[level], message);
    }
  }

  // TODO: Send to external service when configured
  // if (level === 'error' && window.__SENTRY__) { ... }
}

/**
 * Public logging methods
 */
export const logger = {
  debug: (message: string, context?: Record<string, unknown>) => log('debug', message, context),
  info: (message: string, context?: Record<string, unknown>) => log('info', message, context),
  warn: (message: string, context?: Record<string, unknown>) => log('warn', message, context),
  error: (message: string, context?: Record<string, unknown>) => log('error', message, context),
  
  /**
   * Log an error with full stack trace
   */
  captureError: (error: Error, context?: Record<string, unknown>) => {
    const errorId = generateErrorId();
    log('error', error.message, {
      ...context,
      errorId,
      stack: error.stack,
      name: error.name,
    });
    return errorId;
  },

  /**
   * Get recent log entries for debugging
   */
  getRecentLogs: (count: number = 20): LogEntry[] => {
    return logBuffer.slice(-count);
  },

  /**
   * Clear log buffer
   */
  clearLogs: () => {
    logBuffer.length = 0;
  },
};

/**
 * Performance monitoring
 */
export const performance = {
  /**
   * Start a performance measurement
   */
  mark: (name: string): void => {
    performanceMarks.set(name, {
      name,
      startTime: Date.now(),
    });
  },

  /**
   * End a performance measurement and log duration
   */
  measure: (name: string): number | null => {
    const mark = performanceMarks.get(name);
    if (!mark) {
      logger.warn(`Performance mark "${name}" not found`);
      return null;
    }

    const endTime = Date.now();
    const duration = endTime - mark.startTime;
    
    mark.endTime = endTime;
    mark.duration = duration;

    // Log slow operations (> 1 second)
    if (duration > 1000) {
      logger.warn(`Slow operation: ${name}`, { duration: `${duration}ms` });
    } else if (import.meta.env.DEV) {
      logger.debug(`Performance: ${name}`, { duration: `${duration}ms` });
    }

    return duration;
  },

  /**
   * Get all performance measurements
   */
  getMarks: (): PerformanceMark[] => {
    return Array.from(performanceMarks.values());
  },

  /**
   * Clear performance marks
   */
  clearMarks: () => {
    performanceMarks.clear();
  },
};

/**
 * API request tracking
 */
export const apiTracker = {
  /**
   * Track an API request
   */
  trackRequest: async <T>(
    name: string,
    requestFn: () => Promise<T>
  ): Promise<T> => {
    performance.mark(`api:${name}`);
    
    try {
      const result = await requestFn();
      performance.measure(`api:${name}`);
      return result;
    } catch (error) {
      performance.measure(`api:${name}`);
      logger.error(`API request failed: ${name}`, {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  },
};

/**
 * User action tracking (for analytics)
 */
export const analytics = {
  /**
   * Track a user action
   */
  track: (action: string, properties?: Record<string, unknown>): void => {
    logger.info(`User action: ${action}`, properties);
    // TODO: Send to analytics service when configured
  },

  /**
   * Track page view
   */
  pageView: (pageName: string, path: string): void => {
    logger.info(`Page view: ${pageName}`, { path });
    // TODO: Send to analytics service when configured
  },
};

/**
 * Global error handler setup
 */
export function setupGlobalErrorHandlers(): void {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled promise rejection', {
      reason: event.reason instanceof Error ? event.reason.message : String(event.reason),
      stack: event.reason instanceof Error ? event.reason.stack : undefined,
    });
  });

  // Handle global errors
  window.addEventListener('error', (event) => {
    logger.error('Global error', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  logger.info('Global error handlers initialized');
}

// Export types
export type { LogLevel, LogEntry, PerformanceMark };
